/* global btoa, crypto, TextEncoder */

import { API_CONFIG } from "../config/api.js";

export function base64Url(bytes) {
  let binary = "";
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomAuthValue(size = 48) {
  const bytes = new Uint8Array(size);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function createAuthChallenge(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

export async function parseAuthResponse(response) {
  let body = {};
  try {
    body = await response.json();
  } catch (_) {
    // Empty responses are valid for exchange and logout endpoints.
  }
  if (!response.ok) {
    const detail = body?.detail && typeof body.detail === "object" ? body.detail : {};
    const message = detail.detail || detail.error || body?.detail || body?.error;
    const error = new Error(
      typeof message === "string"
        ? message
        : `Authentication failed (${response.status})`,
    );
    error.code = detail.code || body?.code || "AUTH_FAILED";
    error.status = response.status;
    throw error;
  }
  return body;
}

export async function createWebLoginAttempt({ intent = "signin", apiKey = null } = {}) {
  const state = randomAuthValue(32);
  const verifier = randomAuthValue(48);
  const challenge = await createAuthChallenge(verifier);
  const headers = { "Content-Type": "application/json" };
  if (apiKey) headers["X-Api-Key"] = apiKey;

  const response = await fetch(`${API_CONFIG.baseURL}/auth/web/attempts`, {
    method: "POST",
    credentials: "omit",
    headers,
    body: JSON.stringify({ state, challenge, intent }),
  });
  const attempt = await parseAuthResponse(response);
  return {
    attemptId: attempt.attempt_id,
    expiresAt: attempt.expires_at,
    state,
    verifier,
  };
}

export async function exchangeWebLoginAttempt({ attemptId, state, entraToken }) {
  const response = await fetch(
    `${API_CONFIG.baseURL}/auth/web/attempts/${encodeURIComponent(attemptId)}/exchange`,
    {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state, entra_token: entraToken }),
    },
  );
  return parseAuthResponse(response);
}

export async function completeWebLoginAttempt({ attemptId, verifier }) {
  const response = await fetch(
    `${API_CONFIG.baseURL}/auth/web/attempts/${encodeURIComponent(attemptId)}/complete`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ verifier }),
    },
  );
  return parseAuthResponse(response);
}
